import {useEffect, useState} from "react";
import api from "api/axios";
import {Agent} from "types";
import {handleAxiosAndCaughtError} from "utils";
import {CREATE_AGENT_CATEGORIES} from "constants/agents";

interface UseAgentsOptions {
  isLogsAgentPage?: boolean;
}

const useAgents = (options?: UseAgentsOptions) => {
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const isLogsAgentPage = options?.isLogsAgentPage ?? false;

  const fetchAgents = async (signal?: AbortSignal) => {
    setIsLoadingAgents(true);
    setAgentsError(null);
    try {
      const response = await api.get("/agents", {
        params: {
          take: 999,
          skip: 0,
          agent_types: [
            CREATE_AGENT_CATEGORIES.ORCHESTRATOR,
            CREATE_AGENT_CATEGORIES.SPECIALIST,
          ].join(","),
        },
        ...(signal instanceof AbortSignal ? { signal } : {}),
      });
      setAvailableAgents(response.data.items);
    } catch (err) {
      // @ts-expect-error: no prob
      if (err.name === "CanceledError" || signal?.aborted) return;
      setAgentsError("Failed to load agents. Please try again.");
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoadingAgents(false);
    }
  };

  useEffect(() => {
    if (isLogsAgentPage) return;
    const abortController = new AbortController();
    fetchAgents(abortController.signal);
    return () => abortController.abort();
  }, [isLogsAgentPage]);

  return {
    availableAgents,
    isLoadingAgents,
    agentsError,
    fetchAgents,
  };
};

export default useAgents;
